import {
  Button,
  Flex,
  FormLayout,
  Modal,
  StatusBanner,
  statesByCountry,
  getCountryList,
  type CountryCode,
} from "@wayflyer/flyui";
import { RhfDropdownField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStateRegion, getJurisdictionalLabelGetters, type SupportedCountry } from "shared-ui/kyc";

import { addressModalSchema } from "./validation";
import { ModalBodyFitContent } from "./styles";
import type { EditableAddressFormValues, AddressNotification } from "./types";

interface ManualEntryModalProps {
  isOpen: boolean;
  onSave: (values: EditableAddressFormValues) => void;
  onCancel: () => void;
  initialAddress: EditableAddressFormValues | null;
  countries?: CountryCode[];
  lockFields?: Partial<EditableAddressFormValues>;
  notification?: AddressNotification;
}

export const ManualEntryModal = ({
  isOpen,
  onSave,
  onCancel,
  initialAddress,
  countries,
  lockFields,
  notification,
}: ManualEntryModalProps) => {
  const modalForm = useForm<EditableAddressFormValues>({
    resolver: yupResolver(addressModalSchema),
    defaultValues: {
      country_code: "US",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      zip_code: "",
    },
    mode: "all",
  });

  const modalCountryCode = useWatch({
    control: modalForm.control,
    name: "country_code",
  });

  useEffect(() => {
    modalForm.clearErrors("zip_code");
    if (modalForm.formState.dirtyFields.zip_code) {
      void modalForm.trigger("zip_code");
    }
  }, [modalCountryCode, modalForm]);

  useLayoutEffect(() => {
    if (isOpen && initialAddress) {
      modalForm.reset({
        country_code: lockFields?.country_code ?? (initialAddress?.country_code || ""),
        address_line_1: initialAddress.address_line_1 || "",
        address_line_2: initialAddress.address_line_2 || "",
        city: lockFields?.city ?? (initialAddress.city || ""),
        state: lockFields?.state ?? (initialAddress.state || ""),
        zip_code: lockFields?.zip_code ?? (initialAddress.zip_code || ""),
      });
    }
  }, [isOpen, initialAddress, modalForm, lockFields]);

  const handleSaveAddress = async () => {
    const isValid = await modalForm.trigger();
    if (!isValid) {
      return;
    }
    onSave(modalForm.getValues());
  };

  const manualEntryCountryOptions = useMemo(() => {
    return getCountryList(countries as CountryCode[]);
  }, [countries]);

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const countryCode = modalForm.watch("country_code");
  const countryStates = statesByCountry[countryCode] ?? [];
  const stateRegion = useStateRegion(countryCode as SupportedCountry);
  const stateLabel = stateRegion?.label || "State";

  const { getPostCodeLabel } = getJurisdictionalLabelGetters();
  const postalCodeLabel = getPostCodeLabel(countryCode as SupportedCountry);

  return (
    <>
      {isOpen && <ModalBodyFitContent />}
      <Modal isOpen={isOpen} onClose={onCancel} size="normal" title="Enter address manually">
        <FormProvider {...modalForm}>
          <Flex justify="center" align="center" gap="6" direction="column" minWidth="100%">
            {notification && (
              <StatusBanner
                variant={notification.variant}
                title={notification.title}
                subheading={notification.message}
              />
            )}
            <Flex width="100%" direction="column" gap="4">
              <FormLayout>
                <RhfDropdownField
                  form={modalForm}
                  name="country_code"
                  required={true}
                  label="Country"
                  options={manualEntryCountryOptions}
                  disabled={!!lockFields?.country_code}
                />
                <RhfTextField form={modalForm} name="address_line_1" label="Address line 1" required={true} />
                <RhfTextField form={modalForm} name="address_line_2" label="Address line 2" />
                <RhfTextField form={modalForm} name="city" label="City" required={true} />
                {countryStates.length > 0 ? (
                  <RhfDropdownField
                    form={modalForm}
                    name="state"
                    required={true}
                    label={stateLabel}
                    options={countryStates}
                    disabled={!!lockFields?.state}
                  />
                ) : (
                  <RhfTextField
                    form={modalForm}
                    name="state"
                    label={stateLabel}
                    required={true}
                    disabled={!!lockFields?.state}
                  />
                )}
                <RhfTextField
                  form={modalForm}
                  name="zip_code"
                  required={true}
                  label={postalCodeLabel}
                  disabled={!!lockFields?.zip_code}
                />
              </FormLayout>
            </Flex>

            <Flex gap="2" width="100%">
              <Button variant="Tertiary" onClick={onCancel} fullWidth>
                Cancel
              </Button>
              <Button variant="Primary" onClick={handleSaveAddress} fullWidth>
                Save address
              </Button>
            </Flex>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};
